import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Plus, Upload, Hash, Volume2, Sparkles, Server, Users, Image } from 'lucide-react';
import './CreateServerModal.css';

function CreateServerModal({ isOpen, onClose, onCreateServer }) {
  const [step, setStep] = useState(1);
  const [serverName, setServerName] = useState('');
  const [serverDescription, setServerDescription] = useState('');
  const [serverIcon, setServerIcon] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    if (!serverName.trim()) return;
    
    setIsCreating(true);
    try {
      await onCreateServer({
        name: serverName,
        description: serverDescription,
        icon: serverIcon
      });
      handleClose();
    } catch (error) {
      console.error('Failed to create server:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setServerName('');
    setServerDescription('');
    setServerIcon(null);
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}>
          <X size={20} />
        </button>

        {step === 1 && (
          <div className="modal-step">
            <div className="modal-header">
              <h2>Create Your Server</h2>
              <p>Your server is where you and your friends hang out. Make yours and start talking.</p>
            </div>

            <div className="server-options">
              <button className="server-option" onClick={() => setStep(2)}>
                <div className="option-icon">
                  <Server size={24} />
                </div>
                <div className="option-content">
                  <span className="option-title">Create My Own</span>
                  <span className="option-desc">Start fresh with your own server</span>
                </div>
                <Sparkles size={16} className="option-badge" />
              </button>

              <div className="option-divider">
                <span>Or start from a template</span>
              </div>

              <button className="server-option template">
                <div className="option-icon gaming">
                  <Users size={20} />
                </div>
                <span className="option-title">Gaming</span>
              </button>

              <button className="server-option template">
                <div className="option-icon community">
                  <Users size={20} />
                </div>
                <span className="option-title">Community</span>
              </button>

              <button className="server-option template">
                <div className="option-icon friends">
                  <Users size={20} />
                </div>
                <span className="option-title">Friends</span>
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="modal-step">
            <div className="modal-header">
              <h2>Customize Your Server</h2>
              <p>Give your new server a personality with a name and an icon. You can always change it later.</p>
            </div>

            <div className="server-form">
              <div className="icon-upload">
                <div className="icon-preview">
                  {serverIcon ? (
                    <img src={serverIcon} alt="Server icon" />
                  ) : (
                    <>
                      <Upload size={24} />
                      <span>Upload</span>
                    </>
                  )}
                </div>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (e) => setServerIcon(e.target.result);
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </div>

              <div className="form-group">
                <label>Server Name</label>
                <input
                  type="text"
                  placeholder="Enter server name"
                  value={serverName}
                  onChange={(e) => setServerName(e.target.value)}
                  maxLength={100}
                />
              </div>

              <div className="form-group">
                <label>Description <span className="optional">(optional)</span></label>
                <textarea
                  placeholder="What's your server about?"
                  value={serverDescription}
                  onChange={(e) => setServerDescription(e.target.value)}
                  maxLength={500}
                  rows={3}
                />
              </div>

              <div className="default-channels">
                <span className="channels-label">Default channels that will be created:</span>
                <div className="channel-preview">
                  <Hash size={14} /> general
                </div>
                <div className="channel-preview">
                  <Hash size={14} /> announcements
                </div>
                <div className="channel-preview voice">
                  <Volume2 size={14} /> General Voice
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-back" onClick={() => setStep(1)}>
                Back
              </button>
              <button 
                className="btn-create" 
                onClick={handleCreate}
                disabled={!serverName.trim() || isCreating}
              >
                {isCreating ? 'Creating...' : 'Create Server'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default CreateServerModal;
